/**
 * Cohesive design tokens injected into HTML-block iframes.
 *
 * Blocks render in opaque-origin sandboxed iframes, so they cannot inherit
 * the app's CSS custom properties. The token values themselves come straight
 * from the real design-system stylesheets via `COHESIVE_IFRAME_THEME_CSS`
 * (see cohesiveThemeCss.ts) — single source of truth, no hand-ported copy.
 *
 * This module adds the only tokens that genuinely aren't in the stylesheets:
 * the block font stacks (the app loads its fonts through index.html, which an
 * iframe can't see) and the Google Fonts links that make them resolve.
 */

import { assembleBlockTokensCss } from "./blockTokensAssemble";
import { cohesiveThemeCssRaw, cohesiveTokensCssRaw } from "./cohesiveThemeCss";

/** Google Fonts markup mirroring user-dashboard/index.html so block text can
 *  use DM Sans / Source Serif 4. JetBrains Mono is self-hosted by the app and
 *  not reachable cross-origin; --font-mono falls back to system monospace. */
export const COHESIVE_BLOCK_FONT_LINKS =
  '<link rel="preconnect" href="https://fonts.googleapis.com" data-cohesive-injected>' +
  '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin data-cohesive-injected>' +
  '<link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Source+Serif+4:ital,opsz,wght@0,8..60,200..900;1,8..60,200..900&display=swap" rel="stylesheet" data-cohesive-injected>';

export interface CohesiveBlockTokensCssOptions {
  /** Prefix the webfont `@import` rules, for callers that inject this CSS as
   *  a lone stylesheet and therefore can't also add <link> elements. */
  fontImports?: boolean;
}

/**
 * The Cohesive token stylesheet for block iframes, ready to inject into a
 * block's <head>. Ships BOTH light and dark values — the stylesheet's own
 * `.dark` class selector picks the mode, and `prepareSrcDocHtml` sets that
 * class on the iframe's root when `colorScheme: "dark"`. Injected before the
 * document's own styles (and inside `@layer base`) so agent CSS that
 * deliberately redefines a var still wins.
 *
 * Assembly lives in blockTokensAssemble.ts, shared with the generator of the
 * hosted `block-tokens.v1.css` — the stylesheet the backend `<link>`s into
 * stored block HTML so previews/public shares render styled.
 */
export function getCohesiveBlockTokensCss(
  options: CohesiveBlockTokensCssOptions = {},
): string {
  return assembleBlockTokensCss(cohesiveTokensCssRaw, cohesiveThemeCssRaw, {
    fontImports: options.fontImports,
  });
}

/**
 * Token families an agent is taught to style blocks with. Used to recognize a
 * token-styled document on surfaces that can't see the canvas node's
 * `presentation` marker — notably the file-record detail pane, which is
 * addressed by file-record id and has no node data.
 */
const COHESIVE_TOKEN_PATTERN =
  /var\(\s*--(?:n-\d{2}|ink|paper|gold|rust|sage|teal|plum|parchment|spectrum-|sticky-|lc-|text-muted|font-(?:sans|serif|mono))/;

/**
 * True when `html` styles itself with the Cohesive token vocabulary, i.e. it
 * expects the tokens to be injected and the app color scheme to apply.
 * Documents that don't (legacy HTML artifacts) must keep the force-light
 * "paper" treatment — rendering them theme-matched would change how every
 * pre-existing document looks.
 */
export function usesCohesiveBlockTokens(html: string): boolean {
  return COHESIVE_TOKEN_PATTERN.test(html);
}
